<script lang="ts">
  import { onMount } from 'svelte';
  import { afterNavigate, disableScrollHandling, pushState } from '$app/navigation';
  import { page } from '$app/state';
  import { samePageAnchorId, scrollToAnchor } from '$lib/utils/anchor-scroll';
  import MotionToggle from '$lib/components/site/motion-toggle.svelte';
  import { consumeHomeScroll, type HomeScrollPosition } from '$lib/utils/home-scroll';
  import { loadMotionPreference, motionState } from '$lib/utils/motion-state.svelte';

  const EASE_OUT = 'expo.out';

  let configure: (() => void) | undefined;
  let navigationFrame = 0;
  let restartHeroOnReload = false;
  let pendingScroll: HomeScrollPosition | undefined;
  let pendingAnchor: string | undefined;

  afterNavigate(({ type, from, to }) => {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    const hashOnly = type !== 'enter' && from?.url.pathname === to?.url.pathname;
    if (hashOnly) {
      if (to?.url.hash) scrollToAnchor(to.url.hash.slice(1));
      return;
    }

    if (type === 'enter' && to?.url.pathname === '/' && !to.url.hash && navigation?.type === 'reload') {
      disableScrollHandling();
      restartHeroOnReload = true;
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } else if (to?.url.hash) {
      pendingAnchor = to.url.hash.slice(1);
      pendingScroll = undefined;
    } else if (type === 'popstate' && to?.url.pathname === '/') {
      pendingScroll = consumeHomeScroll() ?? { top: scrollY, left: scrollX };
      pendingAnchor = undefined;
    } else {
      pendingScroll = { top: scrollY, left: scrollX };
      pendingAnchor = undefined;
    }
    if (!configure) return;

    cancelAnimationFrame(navigationFrame);
    navigationFrame = requestAnimationFrame(() => configure?.());
  });

  $effect(() => {
    void motionState.paused;
    configure?.();
  });

  function releaseBootState() {
    delete document.documentElement.dataset.motionBoot;
  }

  function handleAnchorClick(event: MouseEvent) {
    const anchorId = samePageAnchorId(event);
    if (!anchorId || !document.getElementById(decodeURIComponent(anchorId))) return;

    event.preventDefault();
    if (location.hash !== `#${anchorId}`) pushState(`#${anchorId}`, page.state);
    requestAnimationFrame(() => scrollToAnchor(anchorId));
  }

  onMount(() => {
    let disposed = false;
    let resizeFrame = 0;
    let viewportCategory = `${innerHeight >= 600}:${innerWidth <= 760}:${innerWidth >= 1060}`;
    let cleanup = () => {};
    const preference = matchMedia('(prefers-reduced-motion: reduce)');
    motionState.reduced = preference.matches;
    loadMotionPreference();

    async function initialise() {
      const [{ gsap }, { ScrollTrigger }, { SplitText }] = await Promise.all([
        import('gsap'),
        import('gsap/ScrollTrigger'),
        import('gsap/SplitText')
      ]);
      await document.fonts.ready;
      if (disposed) return;

      gsap.registerPlugin(ScrollTrigger, SplitText);
      gsap.defaults({ ease: EASE_OUT });

      function present(selector: string) {
        return document.querySelector(selector) !== null;
      }

      function revealOnce(
        targets: gsap.TweenTarget,
        trigger: Element | string,
        vars: gsap.TweenVars,
        start = 'top 88%'
      ) {
        return gsap.from(targets, {
          clearProps: 'transform,opacity,clipPath,filter',
          ...vars,
          scrollTrigger: { trigger, start, once: true }
        });
      }

      function setupHomeStory() {
        const heroScene = document.querySelector<HTMLElement>('.hero-scene');
        if (!heroScene || innerHeight < 600) return;

        const hero = heroScene.parentElement;
        const headerOffset = hero ? hero.getBoundingClientRect().top + scrollY : 0;
        if (hero) gsap.set(hero, { marginTop: -headerOffset });
        gsap.set(heroScene, { attr: { 'data-sequenced': 'true' } });
        const workspace = heroScene.querySelector<HTMLElement>('[data-workspace-entrance]');
        const deviceGroup = heroScene.querySelector<HTMLElement>('.hero-device-group');
        const previewOffset = () => {
          const restingTop = innerWidth <= 760 ? (deviceGroup?.offsetTop ?? 0) : (workspace?.offsetTop ?? 0);
          const peekHeight = Math.min(200, Math.max(130, innerHeight * 0.18));
          return innerHeight - peekHeight - restingTop;
        };
        const heroStory = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            id: 'hero-story',
            trigger: heroScene,
            start: 'top top',
            end: () => `+=${innerHeight * 2.3}`,
            pin: true,
            scrub: 0.8,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onUpdate: updateInkVisibility,
            onScrubComplete: updateInkVisibility
          }
        });
        heroStory
          .to('.hero-curtain', { clipPath: 'ellipse(160% 150% at 50% 100%)', duration: 1, ease: 'power1.inOut' }, 0.1)
          .to('.hero-copy', { y: -80, scale: 0.96, autoAlpha: 0, filter: 'blur(6px)', duration: 0.6 }, 0.2)
          .fromTo(
            '[data-workspace-entrance]',
            { y: previewOffset, autoAlpha: 1, rotationX: 8, scale: 0.94, transformPerspective: 1400 },
            {
              y: 0,
              autoAlpha: 1,
              rotationX: 0,
              scale: () => (innerWidth <= 760 ? 1 : Math.min(1, (innerHeight - 160) / 650)),
              duration: 1.1,
              ease: 'power3.out'
            },
            0.9
          )
          .from(
            '.mobile-live-card, [data-product-note]',
            { y: 40, scale: 0.92, autoAlpha: 0, duration: 0.6, stagger: 0.1, ease: 'back.out(1.6)' },
            1.65
          )
          .to('[data-product-note]', { y: -18, duration: 0.8, stagger: 0.1 }, 2.2)
          .to('.hero-scene .introduction', { clipPath: 'ellipse(160% 150% at 50% 100%)', duration: 0.85 }, 2.65)
          .to('[data-workspace-entrance]', { scale: 0.92, y: -40, duration: 0.85 }, 2.65)
          .from('.hero-scene .introduction-inner', { y: 50, autoAlpha: 0, duration: 0.6, ease: 'power2.out' }, 3.05)
          .set('.hero-product-wrap', { autoAlpha: 0 }, 3.5)
          .to({}, { duration: 0.25 });
      }

      function setupHeroEntrance() {
        gsap.utils.toArray<HTMLElement>('.hero-title-line').forEach((line, index) => {
          if (line.classList.contains('hero-serif')) {
            gsap.from(line.querySelectorAll('.hero-ink'), {
              yPercent: 80,
              opacity: 0,
              rotationX: -35,
              filter: 'blur(8px)',
              duration: 1.15,
              stagger: 0.1,
              delay: 0.1 + index * 0.16
            });
            return;
          }

          const split = SplitText.create(line, { type: 'chars', charsClass: 'hero-character' });
          gsap.from(split.chars, {
            yPercent: 80,
            opacity: 0,
            rotationX: -35,
            transformOrigin: 'center bottom',
            duration: 1.05,
            stagger: 0.022,
            delay: index * 0.16
          });
        });
        if (present('[data-hero-fade]')) {
          gsap.from('[data-hero-fade]', { opacity: 0, y: 18, duration: 0.9, stagger: 0.1, delay: 0.4 });
        }
        if (present('[data-hero-visual]')) {
          gsap.from('[data-hero-visual]', {
            opacity: 0,
            y: 60,
            scale: 0.96,
            rotationX: 6,
            transformPerspective: 1400,
            duration: 1.4,
            delay: 0.45
          });
        }
      }

      function setupHeadingReveals() {
        gsap.utils.toArray<HTMLElement>('main h2, main [data-reveal], .feature-step h3').forEach((heading) => {
          if (heading.closest('.hero-scene[data-sequenced="true"]')) return;
          if (heading.closest('[data-no-reveal]')) return;

          SplitText.create(heading, {
            type: 'lines',
            mask: 'lines',
            autoSplit: true,
            linesClass: 'reveal-line',
            onSplit(split) {
              return gsap.from(split.lines, {
                yPercent: 105,
                rotation: 1.2,
                opacity: 0.35,
                duration: 1,
                stagger: 0.09,
                scrollTrigger: { trigger: heading, start: 'top 92%', once: true }
              });
            }
          });
        });
      }

      function setupGenericReveals() {
        gsap.utils.toArray<HTMLElement>('[data-rise]').forEach((element) => {
          revealOnce(element, element, { y: 32, opacity: 0, duration: 0.95 }, 'top 90%');
        });
        gsap.utils.toArray<HTMLElement>('[data-stagger]').forEach((group) => {
          revealOnce(group.children, group, { y: 28, opacity: 0, duration: 0.85, stagger: 0.08 }, 'top 86%');
        });
        gsap.utils.toArray<HTMLElement>('[data-stage]').forEach((stage) => {
          revealOnce(
            stage,
            stage,
            {
              y: 80,
              scale: 0.93,
              rotationX: 7,
              transformPerspective: 1400,
              transformOrigin: 'center top',
              clipPath: 'inset(6% 0 0 0 round 22px)',
              duration: 1.35
            },
            'top 94%'
          );
        });
        gsap.utils.toArray<HTMLElement>('[data-parallax]').forEach((element) => {
          const distance = Number(element.dataset.parallax) || 40;
          gsap.fromTo(
            element,
            { y: distance },
            {
              y: -distance,
              ease: 'none',
              scrollTrigger: { trigger: element, start: 'top bottom', end: 'bottom top', scrub: 0.6 }
            }
          );
        });
        gsap.utils.toArray<HTMLElement>('[data-marquee]').forEach((track) => {
          const band = track.parentElement ?? track;
          gsap.fromTo(
            track,
            { x: () => window.innerWidth * 0.08 },
            {
              x: () => Math.min(0, window.innerWidth - track.scrollWidth - 24),
              ease: 'none',
              scrollTrigger: {
                trigger: band,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8,
                invalidateOnRefresh: true
              }
            }
          );
          const mark = track.querySelector('svg');
          if (mark) {
            gsap.to(mark, {
              rotation: 180,
              ease: 'none',
              scrollTrigger: { trigger: band, start: 'top bottom', end: 'bottom top', scrub: 0.8 }
            });
          }
        });
      }

      function setupHomeSections(media: gsap.MatchMedia) {
        if (present('.showcase-heading > p')) {
          revealOnce(
            '.showcase-heading > p',
            '.platform-showcase',
            { y: 22, opacity: 0, duration: 0.85, stagger: 0.12 },
            'top 75%'
          );
        }
        gsap.utils.toArray<HTMLElement>('.feature-step').forEach((step) => {
          const description = step.querySelector<HTMLElement>('.feature-description');
          if (!description) return;

          media.add('(min-width: 1000px)', () => {
            revealOnce(
              description.querySelectorAll('.feature-label, p, li'),
              description,
              {
                y: 22,
                opacity: 0,
                duration: 0.85,
                stagger: 0.05
              },
              'top 87%'
            );
          });
        });
        if (present('.showcase-entrance')) {
          revealOnce(
            '.showcase-entrance',
            '.showcase-entrance',
            { y: 70, scale: 0.94, clipPath: 'inset(8% 0 0 0 round 18px)', duration: 1.25 },
            'top 92%'
          );
        }
        gsap.utils.toArray<HTMLElement>('.role-copy > div').forEach((role, index) => {
          revealOnce(role, role, { x: -28, opacity: 0, duration: 0.8, delay: index * 0.05 }, 'top 89%');
        });
        if (present('.role-members > div')) {
          revealOnce(
            '.role-members > div',
            '.roles-visual',
            { x: 28, opacity: 0, duration: 0.75, stagger: 0.1 },
            'top 78%'
          );
        }
        gsap.utils.toArray<HTMLElement>('.index-columns > div').forEach((column) => {
          revealOnce(
            column.querySelectorAll('h3, li'),
            column,
            { y: 18, opacity: 0, duration: 0.75, stagger: 0.05 },
            'top 90%'
          );
        });
        if (present('.questions details')) {
          revealOnce(
            '.questions details',
            '.questions',
            { y: 22, opacity: 0, duration: 0.7, stagger: 0.07 },
            'top 89%'
          );
        }
        if (present('.contact-panel')) {
          revealOnce('.contact-panel', '.contact-panel', { y: 40, opacity: 0, scale: 0.98, duration: 1.1 }, 'top 91%');
        }
        if (present('.closing-content > :not(h2)')) {
          revealOnce(
            '.closing-content > :not(h2)',
            '.closing',
            { y: 22, opacity: 0, duration: 0.85, stagger: 0.08 },
            'top 78%'
          );
        }
        if (!present('.roles-visual')) return;

        media.add('(min-width: 1060px)', () => {
          revealOnce('.roles-visual', '.roles-visual', { rotation: 4, y: 50, scale: 0.94, duration: 1.1 });
        });
        media.add('(max-width: 760px)', () => {
          gsap.fromTo(
            '.roles-visual',
            { y: 35, rotation: 2, scale: 0.96 },
            {
              y: 0,
              rotation: 0,
              scale: 1,
              ease: 'none',
              scrollTrigger: { trigger: '.roles-visual', start: 'top 94%', end: 'top 40%', scrub: 0.6 }
            }
          );
          if (present('.mobile-hero-caption')) {
            gsap.from('.mobile-hero-caption', {
              y: 18,
              opacity: 0.4,
              ease: 'none',
              scrollTrigger: { trigger: '.mobile-hero-caption', start: 'top 96%', end: 'top 76%', scrub: 0.5 }
            });
          }
        });
      }

      configure = () => {
        cleanup();
        const restartHero = restartHeroOnReload;
        const scrollPosition = pendingScroll;
        const anchorId = pendingAnchor;
        restartHeroOnReload = false;
        pendingScroll = undefined;
        pendingAnchor = undefined;
        function restoreNavigationPosition() {
          if (restartHero) {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          } else if (anchorId) {
            scrollToAnchor(anchorId, { smooth: false });
          } else if (scrollPosition) {
            window.scrollTo({ ...scrollPosition, behavior: 'instant' });
          }
        }
        if (restartHero) window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        const paused = motionState.paused || preference.matches;
        document.documentElement.dataset.motion = paused ? 'paused' : 'active';
        if (paused || disposed) {
          releaseBootState();
          restoreNavigationPosition();
          return;
        }

        const media = gsap.matchMedia();
        const context = gsap.context(() => {
          setupHomeStory();
          setupHeroEntrance();
          setupHeadingReveals();
          setupGenericReveals();
          setupHomeSections(media);
          gsap.fromTo(
            '.reading-progress',
            { scaleX: 0 },
            { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: true } }
          );
        });
        releaseBootState();
        cleanup = () => {
          media.revert();
          context.revert();
          const introduction = document.querySelector<HTMLElement>('.hero-scene .introduction');
          introduction?.style.removeProperty('clip-path');
          introduction?.style.removeProperty('visibility');
        };
        updateInkVisibility();
        navigationFrame = requestAnimationFrame(() => {
          ScrollTrigger.clearScrollMemory('manual');
          ScrollTrigger.refresh();
          restoreNavigationPosition();
        });
      };
      configure();
    }
    function updatePreference() {
      motionState.reduced = preference.matches;
      configure?.();
    }
    function updateViewport() {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        const nextCategory = `${innerHeight >= 600}:${innerWidth <= 760}:${innerWidth >= 1060}`;
        if (nextCategory === viewportCategory) return;

        viewportCategory = nextCategory;
        configure?.();
      });
    }
    function updateInkVisibility() {
      const copy = document.querySelector<HTMLElement>('.hero-copy');
      if (!copy) return;

      const bounds = copy.getBoundingClientRect();
      const visible =
        !document.hidden &&
        bounds.bottom > 0 &&
        bounds.top < innerHeight &&
        getComputedStyle(copy).visibility !== 'hidden';
      document.querySelectorAll<HTMLElement>('.hero-ink').forEach((word) => {
        word.style.animationPlayState = visible ? 'running' : 'paused';
      });
    }
    document.addEventListener('visibilitychange', updateInkVisibility);
    document.addEventListener('click', handleAnchorClick, { capture: true });
    window.addEventListener('resize', updateViewport, { passive: true });
    preference.addEventListener('change', updatePreference);
    initialise().catch(() => {
      releaseBootState();
      document.documentElement.dataset.motion = 'paused';
    });
    return () => {
      disposed = true;
      cancelAnimationFrame(navigationFrame);
      cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', updateViewport);
      document.removeEventListener('visibilitychange', updateInkVisibility);
      document.removeEventListener('click', handleAnchorClick, { capture: true });
      cleanup();
      configure = undefined;
      preference.removeEventListener('change', updatePreference);
      delete document.documentElement.dataset.motion;
    };
  });
</script>

<div class="reading-progress" aria-hidden="true"></div>
<div class="motion-tools"><MotionToggle /></div>

<style>
  .reading-progress {
    position: fixed;
    inset: 0 0 auto;
    height: 2px;
    z-index: 45;
    background: linear-gradient(90deg, #1447e6, #5d83fc 60%, #f4edac);
    transform: scaleX(0);
    transform-origin: left;
    pointer-events: none;
  }
  .motion-tools {
    position: fixed;
    bottom: 16px;
    left: 16px;
    z-index: 35;
  }
  @media (max-width: 760px) {
    .motion-tools {
      display: none;
    }
  }
</style>
